/**
 * The narration spoken to THIS child, asked for early and used when it lands.
 *
 * The server (`server/narration.mjs`) speaks every line of the cartoon in the
 * narrator's voice with the name from the funnel in it. That takes a few
 * seconds the first time a name is seen, so it is asked for the moment the
 * name is typed, and by the time the parent reaches the catalogue it is
 * usually there. If it is not — no key on the server, a slow account, an
 * outage — the cartoon plays with the recorded narration, which says Zoya.
 */

const ENDPOINT = '/__narration';

/** Shot (`S01.01`) → URL of the line spoken to this child. */
export type NarrationLines = Record<string, string>;

const cache = new Map<string, Promise<NarrationLines | null>>();
let unavailable = false;

/** Ask once per (cartoon, name); every later call shares the same promise. */
export function requestNarration(cartoon: string, name: string): Promise<NarrationLines | null> {
  const clean = name.trim();
  if (!clean || unavailable) return Promise.resolve(null);
  const key = `${cartoon}|${clean.toLowerCase()}`;
  let p = cache.get(key);
  if (!p) {
    p = (async () => {
      try {
        const r = await fetch(`${ENDPOINT}?cartoon=${encodeURIComponent(cartoon)}&name=${encodeURIComponent(clean)}`);
        if (r.status === 501) { unavailable = true; return null; }
        if (!r.ok) return null;
        const body = (await r.json()) as { ready?: boolean; lines?: NarrationLines };
        return body?.ready && body.lines ? body.lines : null;
      } catch {
        return null;
      }
    })();
    cache.set(key, p);
    // A failed attempt may be retried on the next ask.
    void p.then((lines) => { if (!lines && !unavailable) cache.delete(key); });
  }
  return p;
}
